import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// Generate a simple fingerprint based on browser characteristics
const generateFingerprint = (): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('fingerprint', 2, 2);
  }
  
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.colorDepth,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
    !!window.sessionStorage,
    !!window.localStorage,
    canvas.toDataURL()
  ].join('|');
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(36);
};

// Get or create session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('visitor_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('visitor_session_id', sessionId);
  }
  return sessionId;
};

// Check if returning visitor
const isReturningVisitor = (): boolean => {
  const hasVisited = localStorage.getItem('has_visited');
  if (!hasVisited) {
    localStorage.setItem('has_visited', 'true');
    return false;
  }
  return true;
};

// Detect device type
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Parse user agent to get browser
const getBrowser = (): string => {
  const ua = navigator.userAgent;
  if (ua.indexOf('Firefox') > -1) return 'Firefox';
  if (ua.indexOf('Chrome') > -1) return 'Chrome';
  if (ua.indexOf('Safari') > -1) return 'Safari';
  if (ua.indexOf('Edge') > -1) return 'Edge';
  if (ua.indexOf('MSIE') > -1 || ua.indexOf('Trident') > -1) return 'IE';
  return 'Other';
};

// Parse user agent to get OS
const getOS = (): string => {
  const ua = navigator.userAgent;
  if (ua.indexOf('Win') > -1) return 'Windows';
  if (ua.indexOf('Mac') > -1) return 'MacOS';
  if (ua.indexOf('Linux') > -1) return 'Linux';
  if (ua.indexOf('Android') > -1) return 'Android';
  if (ua.indexOf('iOS') > -1) return 'iOS';
  return 'Other';
};

// Parse UTM parameters from URL
const getUTMParams = (search: string) => {
  const params = new URLSearchParams(search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
  };
};

// Function to identify a visitor with lead information
export const identifyVisitor = async (leadData: {
  email: string;
  name?: string;
  phone?: string;
  company?: string;
  conversionType: string;
}) => {
  try {
    const sessionId = sessionStorage.getItem('visitor_session_id');
    if (!sessionId) return;

    // Update all visitor tracking records for this session with lead information
    const { error } = await supabase
      .from('visitor_tracking')
      .update({
        lead_email: leadData.email,
        lead_name: leadData.name || null,
        lead_phone: leadData.phone || null,
        lead_company: leadData.company || null,
        conversion_type: leadData.conversionType,
      })
      .eq('session_id', sessionId);

    if (error) {
      console.error('Error identifying visitor:', error);
    }
  } catch (err) {
    console.error('Error in visitor identification:', err);
  }
};

export const useVisitorTracking = () => {
  const location = useLocation();

  const sanitizeUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const sensitiveParams = [
        '__lovable_token',
        'token',
        'access_token',
        'session',
        'key',
        'api_key',
        'auth_token'
      ];
      
      sensitiveParams.forEach(param => {
        urlObj.searchParams.delete(param);
      });
      
      return urlObj.toString();
    } catch {
      return url;
    }
  };

  useEffect(() => {
    const trackVisit = async () => {
      try {
        const visitorFingerprint = generateFingerprint();
        const sessionId = getSessionId();
        const utmParams = getUTMParams(location.search);

        const visitData = {
          session_id: sessionId,
          visitor_fingerprint: visitorFingerprint,
          page_url: sanitizeUrl(window.location.href),
          page_title: document.title,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          device_type: getDeviceType(),
          browser: getBrowser(),
          os: getOS(),
          screen_resolution: `${screen.width}x${screen.height}`,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          is_returning_visitor: isReturningVisitor(),
          ...utmParams,
        };

        const { error } = await supabase
          .from('visitor_tracking')
          .insert(visitData);

        if (error) {
          // Silently log error to avoid console pollution - visitor tracking is non-critical
          if (process.env.NODE_ENV === 'development') {
            console.warn('Visitor tracking failed:', error.message);
          }
        }
      } catch (err) {
        // Silently handle network errors - visitor tracking is non-critical
        if (process.env.NODE_ENV === 'development') {
          console.warn('Visitor tracking error:', err instanceof Error ? err.message : 'Unknown error');
        }
      }
    };

    trackVisit();
  }, [location.pathname, location.search]);
};
